import { useState } from "react";
import { authApi } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { fmtUSDFull } from "@/lib/format";
import { Rocket } from "lucide-react";
import { toast } from "sonner";

export default function AutoPilotPanel() {
  const { user, patchUser } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.autopilot_enabled);
  const [budget, setBudget] = useState(Number(user?.autopilot_budget || 100));
  const [minLiq, setMinLiq] = useState(Number(user?.min_liquidity || 0));
  const [saving, setSaving] = useState(false);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const u = await authApi.updateSettings(patch);
      patchUser(patch);
      toast.success("Auto-Pilot updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggle = (v) => { setEnabled(v); persist({ autopilot_enabled: v }); };
  const applyBudget = () => persist({ autopilot_budget: budget });
  const applyMinLiq = () => persist({ min_liquidity: minLiq });

  return (
    <section className="uc-card p-6 uc-fade" data-testid="autopilot-panel">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="uc-chip mb-2"><Rocket size={11} /> AUTO-PILOT</div>
          <h2 className="font-serif-display text-2xl">Autonomous Execution</h2>
          <p className="text-white/50 text-xs mt-1">Executor may act without approval when cost is under the threshold.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`font-mono-uc text-[11px] uppercase tracking-widest ${enabled ? "text-emerald-400" : "text-white/40"}`}>
            {enabled ? "Engaged" : "Standby"}
          </span>
          <Switch
            data-testid="autopilot-toggle"
            checked={enabled}
            onCheckedChange={toggle}
            className="data-[state=checked]:bg-amber-500"
          />
        </div>
      </div>

      <div className="pt-4 border-t border-white/10">
        <div className="flex items-baseline justify-between mb-3">
          <label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Budget Threshold</label>
          <span className="font-serif-display text-3xl" data-testid="autopilot-budget-display">{fmtUSDFull(budget)}</span>
        </div>
        <Slider
          data-testid="autopilot-budget-slider"
          value={[budget]}
          onValueChange={(v) => setBudget(v[0])}
          onValueCommit={applyBudget}
          min={0}
          max={5000}
          step={25}
          className="[&_[role=slider]]:bg-amber-400 [&_[role=slider]]:border-amber-400"
        />
        <div className="flex justify-between font-mono-uc text-[10px] text-white/30 mt-2">
          <span>$0</span><span>$1K</span><span>$2.5K</span><span>$5K</span>
        </div>
      </div>

      <div className="pt-4 border-t border-white/10 mt-4">
        <div className="flex items-baseline justify-between mb-3">
          <label className="font-mono-uc text-[11px] uppercase tracking-widest text-teal-400">Liquidity Floor · Controller Guard</label>
          <span className="font-serif-display text-3xl" data-testid="min-liquidity-display">{fmtUSDFull(minLiq)}</span>
        </div>
        <Slider
          data-testid="min-liquidity-slider"
          value={[minLiq]}
          onValueChange={(v) => setMinLiq(v[0])}
          onValueCommit={applyMinLiq}
          min={0}
          max={250000}
          step={1000}
          className="[&_[role=slider]]:bg-teal-400 [&_[role=slider]]:border-teal-400"
        />
        <div className="flex justify-between font-mono-uc text-[10px] text-white/30 mt-2">
          <span>$0</span><span>$50K</span><span>$150K</span><span>$250K</span>
        </div>
        <div className="font-mono-uc text-[10px] text-white/40 mt-2">
          Controller pauses any Executor action that would drop total cash below this floor.
        </div>
      </div>
    </section>
  );
}
